"use client"
import { useEffect, useState, useRef } from "react"
import { useMemo } from "react"

import { useSelector, useDispatch } from "react-redux"
import { selectAdminDashboardData, fetchAdminDashboardDataAsync } from "../../DashboardData/DashboardDataSlice"
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from "recharts"
import {
    Box,
    Flex,
    Text,
    Heading,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Avatar,
    AvatarBadge,
    Progress,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    SimpleGrid,
    Button,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Tag,
    TagLabel,
    TagLeftIcon,
    Divider,
    useColorModeValue,
    HStack,
    VStack,
    Skeleton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useToast,
    useBreakpointValue,
    Wrap,
    WrapItem,
    Center,
    useColorMode,
    ChakraProvider,
    extendTheme,
} from "@chakra-ui/react"
import {
    Search,
    ChevronDown,
    Settings,
    Plus,
    Check,
    Clock,
    Calendar,
    Eye,
    Edit,
    Download,
    ChevronRight,
    ChevronLeft,
    ArrowUp,
    ArrowRight,
    RefreshCw,
    Briefcase,
    Users,
    Building,
    BarChart2,
    MapPin,
    DollarSign,
} from "lucide-react"

// Custom theme with brand colors
const theme = extendTheme({
    colors: {
        brand: {
            50: "#e6f7ff",
            100: "#b3e0ff",
            200: "#80caff",
            300: "#4db3ff",
            400: "#1a9dff",
            500: "#0087e6",
            600: "#006bb3",
            700: "#004f80",
            800: "#00334d",
            900: "#00171a",
        },
        accent: {
            50: "#fff5e6",
            100: "#ffe0b3",
            200: "#ffcc80",
            300: "#ffb84d",
            400: "#ffa31a",
            500: "#e68a00",
            600: "#b36b00",
            700: "#804c00",
            800: "#4d2e00",
            900: "#1a0f00",
        },
        success: {
            50: "#e6fff5",
            100: "#b3ffe0",
            200: "#80ffcc",
            300: "#4dffb8",
            400: "#1affa3",
            500: "#00e68a",
            600: "#00b36b",
            700: "#00804c",
            800: "#004d2e",
            900: "#001a0f",
        },
    },
    components: {
        Card: {
            baseStyle: {
                container: {
                    borderRadius: "xl",
                    overflow: "hidden",
                    boxShadow: "sm",
                },
            },
        },
        Button: {
            baseStyle: {
                borderRadius: "md",
                fontWeight: "medium",
            },
        },
    },
    fonts: {
        heading: "'Inter', sans-serif",
        body: "'Inter', sans-serif",
    },
    styles: {
        global: (props) => ({
            body: {
                bg: props.colorMode === "dark" ? "gray.900" : "gray.50",
            },
        }),
    },
})

const AdminProfile = () => {
    const dispatch = useDispatch()
    const dashboardData = useSelector(selectAdminDashboardData)
    const [activeTab, setActiveTab] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [selectedJob, setSelectedJob] = useState(null)
    const toast = useToast()
    const { colorMode, toggleColorMode } = useColorMode()
    const [filterStatus, setFilterStatus] = useState("all")
    const [searchQuery, setSearchQuery] = useState("")
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 5

    // Ref to ensure toast is shown only once
    const toastShownRef = useRef(false)

    useEffect(() => {
        const fetchData = async () => {
            await dispatch(fetchAdminDashboardDataAsync())
            setIsLoading(false)
            // Show welcome toast only once

        }
        fetchData()
    }, [dispatch, toast]) // Dependencies are stable, so this runs once on mount

    // Colors for charts
    const COLORS = [
        "#4361ee", // Primary blue
        "#3a0ca3", // Deep purple
        "#7209b7", // Bright purple
        "#f72585", // Pink
        "#4cc9f0", // Light blue
        "#4895ef", // Sky blue
        "#560bad", // Dark purple
        "#f3722c", // Orange
    ]

    // Format data for category pie chart
    const categoryData =
        dashboardData?.topCategories?.data?.map((category) => ({
            name: category.title,
            value: category.jobs_count,
        })) || []

    // Format data for job titles bar chart
    const jobTitleData =
        dashboardData?.trendingJobTitle?.data?.slice(0, 8)?.map((title) => ({
            name: title.title,
            count: title.jobs_count,
        })) || []

    // Format data for candidates/employers comparison
    const userComparisonData = [
        {
            name: "Candidates",
            subscribed: dashboardData?.subscribedCandidates || 0,
            nonSubscribed: Math.max(0, (dashboardData?.totalCandidates || 0) - (dashboardData?.subscribedCandidates || 0)), // Ensure nonSubscribed is not negative
            total: dashboardData?.totalCandidates || 0,
        },
        {
            name: "Employers",
            subscribed: dashboardData?.subscribedEmployers || 0,
            nonSubscribed: Math.max(0, (dashboardData?.totalEmployers || 0) - (dashboardData?.subscribedEmployers || 0)), // Ensure nonSubscribed is not negative
            total: dashboardData?.totalEmployers || 0,
        },
    ]

    // Format data for job type distribution
    const jobTypeData = dashboardData?.recentJobs
        ? Array.from(
            dashboardData.recentJobs.reduce((acc, job) => {
                const type = job.job_type
                acc.set(type, (acc.get(type) || 0) + 1)
                return acc
            }, new Map()),
        ).map(([name, value]) => ({ name, value }))
        : []

    // Format data for job tag distribution
    const jobTagData = dashboardData?.recentJobs
        ? Array.from(
            dashboardData.recentJobs.reduce((acc, job) => {
                const tag = job.job_tag
                acc.set(tag, (acc.get(tag) || 0) + 1)
                return acc
            }, new Map()),
        ).map(([name, value]) => ({ name, value }))
        : []

    // Calculate percentages for stats, capped at 100%
    const activeJobsPercentage = dashboardData?.total_jobs
        ? Math.min(100, Math.round((dashboardData.activeJobs / dashboardData.total_jobs) * 100))
        : 0
    const subscribedCandidatesPercentage = dashboardData?.totalCandidates
        ? Math.min(100, Math.round((dashboardData.subscribedCandidates / dashboardData.totalCandidates) * 100))
        : 0
    const subscribedEmployersPercentage = dashboardData?.totalEmployers
        ? Math.min(100, Math.round((dashboardData.subscribedEmployers / dashboardData.totalEmployers) * 100))
        : 0

    // Calculate max jobs count for progress bars in Top Categories/Job Titles
    const maxCategoryJobs = useMemo(() => {
        return categoryData.reduce((max, item) => Math.max(max, item.value), 0)
    }, [categoryData])

    const maxJobTitleJobs = useMemo(() => {
        return jobTitleData.reduce((max, item) => Math.max(max, item.count), 0)
    }, [jobTitleData])

    // Handle job details modal
    const handleJobClick = (job) => {
        setSelectedJob(job)
        onOpen()
    }

    // Filter jobs based on status and search query
    const filteredJobs = dashboardData?.recentJobs
        ? dashboardData.recentJobs.filter((job) => {
            const matchesStatus = filterStatus === "all" || job.status === filterStatus
            const matchesSearch =
                searchQuery === "" ||
                job.job_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                job.job_title?.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                job.company_name.toLowerCase().includes(searchQuery.toLowerCase())
            return matchesStatus && matchesSearch
        })
        : []

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    const currentItems = filteredJobs.slice(indexOfFirstItem, indexOfLastItem)
    const totalPages = Math.ceil(filteredJobs.length / itemsPerPage)

    // Background colors
    const bgColor = useColorModeValue("gray.50", "gray.900")
    const cardBgColor = useColorModeValue("white", "gray.800")
    const headerBgColor = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.700")
    const accentColor = useColorModeValue("brand.500", "brand.300")
    const textColor = useColorModeValue("gray.800", "white")
    const subtleTextColor = useColorModeValue("gray.600", "gray.400")

    // Responsive design
    const isMobile = useBreakpointValue({ base: true, md: false })
    const statCardColumns = useBreakpointValue({ base: 1, md: 2, lg: 4 })
    const chartColumns = useBreakpointValue({ base: 1, lg: 2 })

    // Color mode values for job details modal
    const postedOnBg = useColorModeValue("gray.50", "gray.700")

    return (
        <ChakraProvider theme={theme}>
            <Box minH="100vh" bg={bgColor} className="min-h-screen  bg-adminbg contentAdmin p-4">
                {/* Main Content */}
                <Flex>
                    {/* Main Dashboard Content */}
                    <Box flex="1" p={{ base: 4, md: 6 }} maxW="100%">
                        {isLoading ? (
                            <VStack spacing={6} align="stretch">
                                <Skeleton height="60px" width="300px" />
                                <SimpleGrid columns={statCardColumns} spacing={6}>
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} height="120px" rounded="xl" />
                                    ))}
                                </SimpleGrid>
                                <Skeleton height="400px" rounded="xl" />
                            </VStack>
                        ) : (
                            <>
                                {/* Page Header */}
                                <Flex
                                    direction={{ base: "column", md: "row" }}
                                    justify="space-between"
                                    align={{ base: "start", md: "center" }}
                                    mb={8}
                                >
                                    <Box mb={{ base: 4, md: 0 }}>
                                        <Heading size="lg" fontWeight="bold" color={textColor} mb={1}>
                                            Admin Dashboard
                                        </Heading>
                                        <Text color={subtleTextColor}>
                                            Welcome back! Here's what's happening with your job portal today.
                                        </Text>
                                    </Box>
                                </Flex>
                                {/* Stats Overview */}
                                <SimpleGrid columns={statCardColumns} spacing={6} mb={8}>
                                    <PremiumStatCard
                                        title="Total Jobs"
                                        value={dashboardData?.total_jobs || 0}
                                        icon={<Briefcase size={18} />}
                                        change={12}
                                        timeframe="from last month"
                                        color="brand"
                                        percentage={activeJobsPercentage}
                                        subtext={`${dashboardData?.activeJobs || 0} active jobs`}
                                    />
                                    <PremiumStatCard
                                        title="Total Candidates"
                                        value={dashboardData?.totalCandidates || 0}
                                        icon={<Users size={18} />}
                                        change={8}
                                        timeframe="from last month"
                                        color="purple"
                                        percentage={subscribedCandidatesPercentage}
                                        subtext={`${dashboardData?.subscribedCandidates || 0} subscribed`}
                                    />
                                    <PremiumStatCard
                                        title="Total Employers"
                                        value={dashboardData?.totalEmployers || 0}
                                        icon={<Building size={18} />}
                                        change={5}
                                        timeframe="from last month"
                                        color="accent"
                                        percentage={subscribedEmployersPercentage}
                                        subtext={`${dashboardData?.subscribedEmployers || 0} subscribed`}
                                    />
                                    <PremiumStatCard
                                        title="Active Jobs"
                                        value={dashboardData?.activeJobs || 0}
                                        icon={<Check size={18} />}
                                        change={-3}
                                        timeframe="from last month"
                                        color="success"
                                        percentage={activeJobsPercentage}
                                        subtext={`${activeJobsPercentage}% of total jobs`}
                                    />
                                </SimpleGrid>
                                {/* Main Content with Tabs */}
                                <Card
                                    borderRadius="xl"
                                    boxShadow="sm"
                                    overflow="hidden"
                                    borderColor={borderColor}
                                    bg={cardBgColor}
                                    mb={8}
                                >
                                    <Tabs
                                        variant="soft-rounded"
                                        colorScheme="brand"
                                        onChange={(index) => setActiveTab(index)}
                                        isLazy
                                        p={4}
                                    >
                                        <TabList mb={4} overflowX="auto" py={2} css={{ scrollbarWidth: "none" }}>
                                            {[
                                                { label: "Overview", icon: <Eye size={18} /> },
                                                { label: "Jobs", icon: <Briefcase size={18} /> },
                                                { label: "Users", icon: <Users size={18} /> },
                                                { label: "Analytics", icon: <BarChart2 size={18} /> },
                                            ].map((tab, index) => (
                                                <Tab
                                                    key={index}
                                                    _selected={{ color: "white", bg: "brand.500" }}
                                                    borderRadius="full"
                                                    mr={2}
                                                    fontWeight="medium"
                                                >
                                                    <HStack spacing={2} align="center">
                                                        <div display="inline-flex" className="inline-flex items-start" alignItems="center">
                                                            {tab.icon}
                                                        </div>
                                                        <div display="inline-flex" className="inline-flex items-start" alignItems="center">
                                                            {tab.label}
                                                        </div>
                                                    </HStack>
                                                </Tab>
                                            ))}
                                        </TabList>
                                        <TabPanels>
                                            {/* Overview Tab */}
                                            <TabPanel px={0}>
                                                <VStack spacing={6} align="stretch">
                                                    {/* Featured Stats */}
                                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                                        <FeaturedStat
                                                            title="Subscription Rate"
                                                            value={`${subscribedCandidatesPercentage}%`}
                                                            icon={<Check size={16} />}
                                                            description="of candidates are subscribed"
                                                            color="green.500"
                                                        />
                                                        <FeaturedStat
                                                            title="Average Salary"
                                                            value="₹850,000"
                                                            icon={<DollarSign size={16} />}
                                                            description="across all active jobs"
                                                            color="purple.500"
                                                        />
                                                        <FeaturedStat
                                                            title="Job Fill Rate"
                                                            value="68%"
                                                            icon={<Clock size={16} />}
                                                            description="jobs filled within 30 days"
                                                            color="blue.500"
                                                        />
                                                    </SimpleGrid>
                                                    {/* Charts Section */}
                                                    <SimpleGrid columns={chartColumns} spacing={6}>
                                                        {/* Job Categories Chart */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Flex justify="space-between" align="center">
                                                                    <Heading size="md" fontWeight="semibold">
                                                                        Top Job Categories
                                                                    </Heading>
                                                                    <Menu>
                                                                        <MenuButton
                                                                            as={IconButton}
                                                                            aria-label="Options"
                                                                            icon={<Settings size={16} />}
                                                                            variant="ghost"
                                                                            size="sm"
                                                                        />
                                                                        <MenuList>
                                                                            <MenuItem icon={<Download size={16} />}>Download CSV</MenuItem>
                                                                            <MenuItem icon={<RefreshCw size={16} />}>Refresh Data</MenuItem>
                                                                            <MenuItem icon={<Eye size={16} />}>View Full Report</MenuItem>
                                                                        </MenuList>
                                                                    </Menu>
                                                                </Flex>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="400px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <PieChart>
                                                                            <Pie
                                                                                data={categoryData}
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                labelLine={false}
                                                                                outerRadius={100}
                                                                                fill="#8884d8"
                                                                                dataKey="value"
                                                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                                            >
                                                                                {categoryData.map((entry, index) => (
                                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                                ))}
                                                                            </Pie>
                                                                            <RechartsTooltip formatter={(value) => [`${value} jobs`, "Count"]} />
                                                                            <Legend />
                                                                        </PieChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                        {/* Trending Job Titles Chart */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Flex justify="space-between" align="center">
                                                                    <Heading size="md" fontWeight="semibold">
                                                                        Trending Job Titles
                                                                    </Heading>
                                                                    <Menu>
                                                                        <MenuButton
                                                                            as={IconButton}
                                                                            aria-label="Options"
                                                                            icon={<Settings size={16} />}
                                                                            variant="ghost"
                                                                            size="sm"
                                                                        />
                                                                        <MenuList>
                                                                            <MenuItem icon={<Download size={16} />}>Download CSV</MenuItem>
                                                                            <MenuItem icon={<RefreshCw size={16} />}>Refresh Data</MenuItem>
                                                                            <MenuItem icon={<Eye size={16} />}>View Full Report</MenuItem>
                                                                        </MenuList>
                                                                    </Menu>
                                                                </Flex>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="400px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <BarChart
                                                                            data={jobTitleData}
                                                                            margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                                                                            barSize={20}
                                                                        >
                                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                            <XAxis
                                                                                dataKey="name"
                                                                                angle={-45}
                                                                                textAnchor="end"
                                                                                height={70}
                                                                                interval={0}
                                                                                tick={{ fontSize: 12 }}
                                                                            />
                                                                            <YAxis />
                                                                            <RechartsTooltip
                                                                                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                                                                                formatter={(value) => [`${value} jobs`, "Count"]}
                                                                            />
                                                                            <Bar
                                                                                dataKey="count"
                                                                                fill="#4361ee"
                                                                                name="Jobs"
                                                                                radius={[4, 4, 0, 0]}
                                                                                background={{ fill: "#eee", radius: [4, 4, 0, 0] }}
                                                                            />
                                                                        </BarChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* Recent Jobs */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardHeader>
                                                            <Flex justify="space-between" align="center">
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Recent Jobs
                                                                </Heading>
                                                                <Button
                                                                    size="sm"
                                                                    colorScheme="brand"
                                                                    variant="ghost"
                                                                    rightIcon={<ArrowRight size={16} />}
                                                                >
                                                                    View All
                                                                </Button>
                                                            </Flex>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <Box overflowX="auto">
                                                                <PremiumJobsTable
                                                                    jobs={dashboardData?.recentJobs?.slice(0, 3) || []}
                                                                    onJobClick={handleJobClick}
                                                                />
                                                            </Box>
                                                        </CardBody>
                                                    </Card>
                                                    {/* Subscription Status */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardHeader pb={0}>
                                                            <Flex justify="space-between" align="center">
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Subscription Status
                                                                </Heading>
                                                                <Menu>
                                                                    <MenuButton
                                                                        as={IconButton}
                                                                        aria-label="Options"
                                                                        icon={<Settings size={16} />}
                                                                        variant="ghost"
                                                                        size="sm"
                                                                    />
                                                                    <MenuList>
                                                                        <MenuItem icon={<Download size={16} />}>Download CSV</MenuItem>
                                                                        <MenuItem icon={<RefreshCw size={16} />}>Refresh Data</MenuItem>
                                                                        <MenuItem icon={<Eye size={16} />}>View Full Report</MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            </Flex>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <Box h="300px">
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <BarChart
                                                                        data={userComparisonData}
                                                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                                        barSize={40}
                                                                    >
                                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                        <XAxis dataKey="name" />
                                                                        <YAxis />
                                                                        <RechartsTooltip cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
                                                                        <Legend />
                                                                        <Bar
                                                                            dataKey="subscribed"
                                                                            stackId="a"
                                                                            fill="#4cc9f0"
                                                                            name="Subscribed"
                                                                            radius={[4, 4, 0, 0]}
                                                                        />
                                                                        <Bar
                                                                            dataKey="nonSubscribed"
                                                                            stackId="a"
                                                                            fill="#f72585"
                                                                            name="Non-Subscribed"
                                                                            radius={[0, 0, 4, 4]}
                                                                        />
                                                                    </BarChart>
                                                                </ResponsiveContainer>
                                                            </Box>
                                                        </CardBody>
                                                    </Card>
                                                </VStack>
                                            </TabPanel>
                                            {/* Jobs Tab */}
                                            <TabPanel px={0}>
                                                <VStack spacing={6} align="stretch">
                                                    {/* Job Filters */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardBody>
                                                            <Flex
                                                                direction={{ base: "column", md: "row" }}
                                                                justify="space-between"
                                                                align={{ base: "stretch", md: "center" }}
                                                                gap={4}
                                                            >
                                                                <InputGroup maxW={{ md: "320px" }}>
                                                                    <InputLeftElement pointerEvents="none">
                                                                        <Search size={16} color="gray.400" />
                                                                    </InputLeftElement>
                                                                    <Input
                                                                        placeholder="Search jobs..."
                                                                        value={searchQuery}
                                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                                        borderRadius="full"
                                                                    />
                                                                </InputGroup>
                                                                <HStack spacing={4}>
                                                                    <Select
                                                                        placeholder="Filter by status"
                                                                        size="md"
                                                                        borderRadius="full"
                                                                        value={filterStatus}
                                                                        onChange={(e) => setFilterStatus(e.target.value)}
                                                                        w={{ base: "full", md: "200px" }}
                                                                    >
                                                                        <option value="all">All Jobs</option>
                                                                        <option value="active">Active</option>
                                                                        <option value="completed">Completed</option>
                                                                        <option value="draft">Draft</option>
                                                                    </Select>
                                                                    <Button
                                                                        leftIcon={<Plus size={16} />}
                                                                        colorScheme="brand"
                                                                        borderRadius="full"
                                                                        display={{ base: "none", md: "flex" }}
                                                                    >
                                                                        Add Job
                                                                    </Button>
                                                                </HStack>
                                                            </Flex>
                                                        </CardBody>
                                                    </Card>
                                                    {/* Job Type & Tag Distribution */}
                                                    <SimpleGrid columns={chartColumns} spacing={6}>
                                                        {/* Job Type Distribution */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Job Type Distribution
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="300px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <PieChart>
                                                                            <Pie
                                                                                data={jobTypeData}
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                labelLine={false}
                                                                                outerRadius={100}
                                                                                fill="#8884d8"
                                                                                dataKey="value"
                                                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                                            >
                                                                                {jobTypeData.map((entry, index) => (
                                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                                ))}
                                                                            </Pie>
                                                                            <RechartsTooltip formatter={(value) => [`${value} jobs`, "Count"]} />
                                                                            <Legend />
                                                                        </PieChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                        {/* Job Tag Distribution */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Job Tag Distribution
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="300px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <PieChart>
                                                                            <Pie
                                                                                data={jobTagData}
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                labelLine={false}
                                                                                outerRadius={100}
                                                                                fill="#8884d8"
                                                                                dataKey="value"
                                                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                                            >
                                                                                {jobTagData.map((entry, index) => (
                                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                                ))}
                                                                            </Pie>
                                                                            <RechartsTooltip formatter={(value) => [`${value} jobs`, "Count"]} />
                                                                            <Legend />
                                                                        </PieChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* Jobs Table */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardHeader>
                                                            <Flex justify="space-between" align="center">
                                                                <Heading size="md" fontWeight="semibold">
                                                                    All Jobs
                                                                </Heading>
                                                                <HStack>
                                                                    <Button
                                                                        leftIcon={<Download size={16} />}
                                                                        variant="outline"
                                                                        size="sm"
                                                                        borderRadius="full"
                                                                    >
                                                                        Export
                                                                    </Button>
                                                                </HStack>
                                                            </Flex>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <Box overflowX="auto">
                                                                <PremiumJobsTable jobs={currentItems} onJobClick={handleJobClick} />
                                                            </Box>
                                                        </CardBody>
                                                        <CardFooter borderTopWidth="1px" borderColor={borderColor}>
                                                            <Flex width="100%" justify="space-between" align="center">
                                                                <Text fontSize="sm" color={subtleTextColor}>
                                                                    Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredJobs.length)} of{" "}
                                                                    {filteredJobs.length} jobs
                                                                </Text>
                                                                <HStack>
                                                                    <IconButton
                                                                        icon={<ChevronLeft size={16} />}
                                                                        aria-label="Previous page"
                                                                        size="sm"
                                                                        isDisabled={currentPage === 1}
                                                                        onClick={() => setCurrentPage(currentPage - 1)}
                                                                        variant="ghost"
                                                                    />
                                                                    <Text fontSize="sm">
                                                                        Page {currentPage} of {totalPages}
                                                                    </Text>
                                                                    <IconButton
                                                                        icon={<ChevronRight size={16} />}
                                                                        aria-label="Next page"
                                                                        size="sm"
                                                                        isDisabled={currentPage === totalPages}
                                                                        onClick={() => setCurrentPage(currentPage + 1)}
                                                                        variant="ghost"
                                                                    />
                                                                </HStack>
                                                            </Flex>
                                                        </CardFooter>
                                                    </Card>
                                                </VStack>
                                            </TabPanel>
                                            {/* Users Tab */}
                                            <TabPanel px={0}>
                                                <VStack spacing={6} align="stretch">
                                                    {/* User Distribution */}
                                                    <SimpleGrid columns={chartColumns} spacing={6}>
                                                        {/* Candidates vs Employers */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    User Distribution
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="400px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <BarChart
                                                                            data={userComparisonData}
                                                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                                            barSize={60}
                                                                        >
                                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                            <XAxis dataKey="name" />
                                                                            <YAxis />
                                                                            <RechartsTooltip cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
                                                                            <Legend />
                                                                            <Bar
                                                                                dataKey="total"
                                                                                fill="#4361ee"
                                                                                name="Total Users"
                                                                                radius={[4, 4, 0, 0]}
                                                                                background={{ fill: "#eee", radius: [4, 4, 0, 0] }}
                                                                            />
                                                                        </BarChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                        {/* Subscription Ratio */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Subscription Ratio
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="400px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <PieChart>
                                                                            <Pie
                                                                                data={[
                                                                                    {
                                                                                        name: "Subscribed Candidates",
                                                                                        value: dashboardData?.subscribedCandidates || 0,
                                                                                    },
                                                                                    {
                                                                                        name: "Non-Subscribed Candidates",
                                                                                        value: Math.max(
                                                                                            0,
                                                                                            (dashboardData?.totalCandidates || 0) -
                                                                                            (dashboardData?.subscribedCandidates || 0),
                                                                                        ),
                                                                                    },
                                                                                    {
                                                                                        name: "Subscribed Employers",
                                                                                        value: dashboardData?.subscribedEmployers || 0,
                                                                                    },
                                                                                    {
                                                                                        name: "Non-Subscribed Employers",
                                                                                        value: Math.max(
                                                                                            0,
                                                                                            (dashboardData?.totalEmployers || 0) -
                                                                                            (dashboardData?.subscribedEmployers || 0),
                                                                                        ),
                                                                                    },
                                                                                ]}
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                labelLine={false}
                                                                                outerRadius={100}
                                                                                fill="#8884d8"
                                                                                dataKey="value"
                                                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                                            >
                                                                                <Cell fill="#4cc9f0" />
                                                                                <Cell fill="#f72585" />
                                                                                <Cell fill="#4361ee" />
                                                                                <Cell fill="#7209b7" />
                                                                            </Pie>
                                                                            <RechartsTooltip formatter={(value) => [`${value} users`, "Count"]} />
                                                                            <Legend />
                                                                        </PieChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* User Stats */}
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Candidate Stats
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <VStack spacing={6} align="stretch">
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Total Candidates</Text>
                                                                            <Text fontWeight="bold">{dashboardData?.totalCandidates || 0}</Text>
                                                                        </Flex>
                                                                        <Progress value={100} size="sm" colorScheme="brand" borderRadius="full" hasStripe />
                                                                    </Box>
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Subscribed</Text>
                                                                            <HStack align={"center"}>
                                                                                <div className="font-semibold" fontWeight="bold">
                                                                                    {dashboardData?.subscribedCandidates || 0}
                                                                                </div>
                                                                                <Badge colorScheme="green" borderRadius="full">
                                                                                    {subscribedCandidatesPercentage}%
                                                                                </Badge>
                                                                            </HStack>
                                                                        </Flex>
                                                                        <Progress
                                                                            value={subscribedCandidatesPercentage}
                                                                            size="sm"
                                                                            colorScheme="green"
                                                                            borderRadius="full"
                                                                            hasStripe
                                                                        />
                                                                    </Box>
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Non-Subscribed</Text>
                                                                            <HStack align={"center"}>
                                                                                <div fontWeight="bold" className="font-bold">
                                                                                    {Math.max(
                                                                                        0,
                                                                                        (dashboardData?.totalCandidates || 0) -
                                                                                        (dashboardData?.subscribedCandidates || 0),
                                                                                    )}
                                                                                </div>
                                                                                <Badge colorScheme="red" borderRadius="full">
                                                                                    {Math.max(0, 100 - subscribedCandidatesPercentage)}%
                                                                                </Badge>
                                                                            </HStack>
                                                                        </Flex>
                                                                        <Progress
                                                                            value={Math.max(0, 100 - subscribedCandidatesPercentage)}
                                                                            size="sm"
                                                                            colorScheme="red"
                                                                            borderRadius="full"
                                                                            hasStripe
                                                                        />
                                                                    </Box>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Employer Stats
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <VStack spacing={6} align="stretch">
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Total Employers</Text>
                                                                            <Text fontWeight="bold">{dashboardData?.totalEmployers || 0}</Text>
                                                                        </Flex>
                                                                        <Progress value={100} size="sm" colorScheme="brand" borderRadius="full" hasStripe />
                                                                    </Box>
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Subscribed</Text>
                                                                            <HStack align={"center"}>
                                                                                <div className="font-bold" fontWeight="bold">
                                                                                    {dashboardData?.subscribedEmployers || 0}
                                                                                </div>
                                                                                <Badge colorScheme="green" borderRadius="full">
                                                                                    {subscribedEmployersPercentage}%
                                                                                </Badge>
                                                                            </HStack>
                                                                        </Flex>
                                                                        <Progress
                                                                            value={subscribedEmployersPercentage}
                                                                            size="sm"
                                                                            colorScheme="green"
                                                                            borderRadius="full"
                                                                            hasStripe
                                                                        />
                                                                    </Box>
                                                                    <Box>
                                                                        <Flex justify="space-between" mb={2}>
                                                                            <Text fontWeight="medium">Non-Subscribed</Text>
                                                                            <HStack align={"center"}>
                                                                                <div className="font-bold" fontWeight="bold">
                                                                                    {Math.max(
                                                                                        0,
                                                                                        (dashboardData?.totalEmployers || 0) -
                                                                                        (dashboardData?.subscribedEmployers || 0),
                                                                                    )}
                                                                                </div>
                                                                                <Badge colorScheme="red" borderRadius="full">
                                                                                    {Math.max(0, 100 - subscribedEmployersPercentage)}%
                                                                                </Badge>
                                                                            </HStack>
                                                                        </Flex>
                                                                        <Progress
                                                                            value={Math.max(0, 100 - subscribedEmployersPercentage)}
                                                                            size="sm"
                                                                            colorScheme="red"
                                                                            borderRadius="full"
                                                                            hasStripe
                                                                        />
                                                                    </Box>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* User Activity */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardHeader>
                                                            <Flex justify="space-between" align="center">
                                                                <Heading size="md" fontWeight="semibold">
                                                                    User Activity
                                                                </Heading>
                                                                <Menu>
                                                                    <MenuButton
                                                                        as={Button}
                                                                        rightIcon={<ChevronDown size={16} />}
                                                                        size="sm"
                                                                        variant="outline"
                                                                        borderRadius="full"
                                                                    >
                                                                        Last 6 Months
                                                                    </MenuButton>
                                                                    <MenuList>
                                                                        <MenuItem>Last 30 Days</MenuItem>
                                                                        <MenuItem>Last 3 Months</MenuItem>
                                                                        <MenuItem>Last 6 Months</MenuItem>
                                                                        <MenuItem>Last Year</MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            </Flex>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <Box h="300px">
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <AreaChart
                                                                        data={[
                                                                            { name: "Jan", candidates: 65, employers: 45 },
                                                                            { name: "Feb", candidates: 70, employers: 48 },
                                                                            { name: "Mar", candidates: 75, employers: 52 },
                                                                            { name: "Apr", candidates: 82, employers: 55 },
                                                                            { name: "May", candidates: 90, employers: 60 },
                                                                            { name: "Jun", candidates: 99, employers: 69 },
                                                                        ]}
                                                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                                                    >
                                                                        <defs>
                                                                            <linearGradient id="colorCandidates" x1="0" y1="0" x2="0" y2="1">
                                                                                <stop offset="5%" stopColor="#4361ee" stopOpacity={0.8} />
                                                                                <stop offset="95%" stopColor="#4361ee" stopOpacity={0} />
                                                                            </linearGradient>
                                                                            <linearGradient id="colorEmployers" x1="0" y1="0" x2="0" y2="1">
                                                                                <stop offset="5%" stopColor="#f72585" stopOpacity={0.8} />
                                                                                <stop offset="95%" stopColor="#f72585" stopOpacity={0} />
                                                                            </linearGradient>
                                                                        </defs>
                                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                        <XAxis dataKey="name" />
                                                                        <YAxis />
                                                                        <RechartsTooltip />
                                                                        <Legend />
                                                                        <Area
                                                                            type="monotone"
                                                                            dataKey="candidates"
                                                                            stroke="#4361ee"
                                                                            fillOpacity={1}
                                                                            fill="url(#colorCandidates)"
                                                                            name="Candidates"
                                                                        />
                                                                        <Area
                                                                            type="monotone"
                                                                            dataKey="employers"
                                                                            stroke="#f72585"
                                                                            fillOpacity={1}
                                                                            fill="url(#colorEmployers)"
                                                                            name="Employers"
                                                                        />
                                                                    </AreaChart>
                                                                </ResponsiveContainer>
                                                            </Box>
                                                        </CardBody>
                                                    </Card>
                                                </VStack>
                                            </TabPanel>
                                            {/* Analytics Tab */}
                                            <TabPanel px={0}>
                                                <VStack spacing={6} align="stretch">
                                                    {/* Analytics Overview */}
                                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                                        <FeaturedStat
                                                            title="Conversion Rate"
                                                            value="24.8%"
                                                            icon={<ArrowUp size={16} />}
                                                            description="applications to hires"
                                                            color="green.500"
                                                        />
                                                        <FeaturedStat
                                                            title="Avg. Time to Hire"
                                                            value="18 days"
                                                            icon={<Clock size={16} />}
                                                            description="across all positions"
                                                            color="purple.500"
                                                        />
                                                        <FeaturedStat
                                                            title="Employer Retention"
                                                            value="86%"
                                                            icon={<RefreshCw size={16} />}
                                                            description="annual renewal rate"
                                                            color="blue.500"
                                                        />
                                                    </SimpleGrid>
                                                    {/* Job Skills Radar Chart */}
                                                    <SimpleGrid columns={chartColumns} spacing={6}>
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Job Skills Distribution
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="300px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <RadarChart
                                                                            outerRadius={90}
                                                                            data={[
                                                                                { subject: "Software Dev", A: 120, fullMark: 150 },
                                                                                { subject: "HR", A: 98, fullMark: 150 },
                                                                                { subject: "Admin", A: 86, fullMark: 150 },
                                                                                { subject: "Management", A: 99, fullMark: 150 },
                                                                                { subject: "Customer Service", A: 85, fullMark: 150 },
                                                                                { subject: "Legal", A: 65, fullMark: 150 },
                                                                            ]}
                                                                        >
                                                                            <PolarGrid />
                                                                            <PolarAngleAxis dataKey="subject" />
                                                                            <PolarRadiusAxis angle={30} domain={[0, 150]} />
                                                                            <Radar
                                                                                name="Skills"
                                                                                dataKey="A"
                                                                                stroke="#4361ee"
                                                                                fill="#4361ee"
                                                                                fillOpacity={0.6}
                                                                            />
                                                                            <Legend />
                                                                        </RadarChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                        {/* Job Growth Trend */}
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader pb={0}>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Job Growth Trend
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <Box h="300px">
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <LineChart
                                                                            data={[
                                                                                { name: "Jan", jobs: 40 },
                                                                                { name: "Feb", jobs: 45 },
                                                                                { name: "Mar", jobs: 48 },
                                                                                { name: "Apr", jobs: 52 },
                                                                                { name: "May", jobs: 58 },
                                                                                { name: "Jun", jobs: 64 },
                                                                            ]}
                                                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                                                        >
                                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                            <XAxis dataKey="name" />
                                                                            <YAxis />
                                                                            <RechartsTooltip />
                                                                            <Legend />
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="jobs"
                                                                                stroke="#4361ee"
                                                                                activeDot={{ r: 8 }}
                                                                                name="Total Jobs"
                                                                                strokeWidth={2}
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* Top Categories and Job Titles */}
                                                    <SimpleGrid columns={chartColumns} spacing={6}>
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Top Categories
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <VStack spacing={6} align="stretch">
                                                                    {dashboardData?.topCategories?.data?.map((category, index) => (
                                                                        <Box key={index}>
                                                                            <Flex justify="space-between" mb={2}>
                                                                                <Text fontWeight="medium">{category.title}</Text>
                                                                                <HStack>
                                                                                    <Text fontWeight="bold">{category.jobs_count} jobs</Text>
                                                                                    <Badge colorScheme={getProgressColor(index)} borderRadius="full">
                                                                                        {maxCategoryJobs > 0
                                                                                            ? Math.round((category.jobs_count / maxCategoryJobs) * 100)
                                                                                            : 0}
                                                                                        %
                                                                                    </Badge>
                                                                                </HStack>
                                                                            </Flex>
                                                                            <Progress
                                                                                value={maxCategoryJobs > 0 ? (category.jobs_count / maxCategoryJobs) * 100 : 0}
                                                                                size="sm"
                                                                                colorScheme={getProgressColor(index)}
                                                                                borderRadius="full"
                                                                                hasStripe
                                                                            />
                                                                        </Box>
                                                                    ))}
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                            <CardHeader>
                                                                <Heading size="md" fontWeight="semibold">
                                                                    Top Job Titles
                                                                </Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                <VStack spacing={6} align="stretch">
                                                                    {dashboardData?.trendingJobTitle?.data?.slice(0, 6).map((title, index) => (
                                                                        <Box key={index}>
                                                                            <Flex justify="space-between" mb={2}>
                                                                                <Text fontWeight="medium">{title.title}</Text>
                                                                                <HStack>
                                                                                    <Text fontWeight="bold">{title.jobs_count} jobs</Text>
                                                                                    <Badge colorScheme={getProgressColor(index)} borderRadius="full">
                                                                                        {maxJobTitleJobs > 0
                                                                                            ? Math.round((title.jobs_count / maxJobTitleJobs) * 100)
                                                                                            : 0}
                                                                                        %
                                                                                    </Badge>
                                                                                </HStack>
                                                                            </Flex>
                                                                            <Progress
                                                                                value={maxJobTitleJobs > 0 ? (title.jobs_count / maxJobTitleJobs) * 100 : 0}
                                                                                size="sm"
                                                                                colorScheme={getProgressColor(index)}
                                                                                borderRadius="full"
                                                                                hasStripe
                                                                            />
                                                                        </Box>
                                                                    ))}
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                    {/* Summary Stats */}
                                                    <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
                                                        <CardHeader>
                                                            <Heading size="md" fontWeight="semibold">
                                                                Summary Statistics
                                                            </Heading>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                                                                <Stat>
                                                                    <StatLabel fontWeight="medium">Total Jobs</StatLabel>
                                                                    <StatNumber color={accentColor}>{dashboardData?.total_jobs || 0}</StatNumber>
                                                                    <StatHelpText>
                                                                        <StatArrow type="increase" />
                                                                        {activeJobsPercentage}%
                                                                    </StatHelpText>
                                                                </Stat>
                                                                <Stat>
                                                                    <StatLabel fontWeight="medium">Active Jobs</StatLabel>
                                                                    <StatNumber color="green.500">{dashboardData?.activeJobs || 0}</StatNumber>
                                                                    <StatHelpText>
                                                                        <StatArrow type="increase" />
                                                                        {activeJobsPercentage}%
                                                                    </StatHelpText>
                                                                </Stat>
                                                                <Stat>
                                                                    <StatLabel fontWeight="medium">Total Candidates</StatLabel>
                                                                    <StatNumber color="purple.500">{dashboardData?.totalCandidates || 0}</StatNumber>
                                                                    <StatHelpText>
                                                                        <StatArrow type="increase" />
                                                                        {subscribedCandidatesPercentage}%
                                                                    </StatHelpText>
                                                                </Stat>
                                                                <Stat>
                                                                    <StatLabel fontWeight="medium">Total Employers</StatLabel>
                                                                    <StatNumber color="orange.500">{dashboardData?.totalEmployers || 0}</StatNumber>
                                                                    <StatHelpText>
                                                                        <StatArrow type="increase" />
                                                                        {subscribedEmployersPercentage}%
                                                                    </StatHelpText>
                                                                </Stat>
                                                            </SimpleGrid>
                                                        </CardBody>
                                                    </Card>
                                                </VStack>
                                            </TabPanel>
                                        </TabPanels>
                                    </Tabs>
                                </Card>
                            </>
                        )}
                    </Box>
                </Flex>
            </Box>
            {/* Job Details Modal */}
            {selectedJob && (
                <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
                    <ModalOverlay backdropFilter="blur(2px)" />
                    <ModalContent borderRadius="xl">
                        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
                            Job Details
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody py={6}>
                            <VStack spacing={6} align="stretch">
                                <Flex>
                                    <Avatar
                                        size="xl"
                                        name={selectedJob.company_name}
                                        src={selectedJob.company_logo ? `/company-logos/${selectedJob.company_logo}` : ""}
                                        bg="brand.100"
                                        mr={4}
                                        borderWidth="2px"
                                        borderColor={selectedJob.status === "active" ? "green.500" : "gray.300"}
                                    />
                                    <Box>
                                        <Heading size="md">{selectedJob.job_name}</Heading>
                                        <Text color={subtleTextColor} fontWeight="medium">
                                            {selectedJob.job_title?.title}
                                        </Text>
                                        <Text fontSize="sm" color={subtleTextColor} mt={1}>
                                            {selectedJob.company_name}
                                        </Text>
                                        <HStack mt={2} spacing={2}>
                                            <Badge
                                                colorScheme={
                                                    selectedJob.status === "active"
                                                        ? "green"
                                                        : selectedJob.status === "completed"
                                                            ? "blue"
                                                            : "gray"
                                                }
                                                borderRadius="full"
                                                px={2}
                                            >
                                                {selectedJob.status}
                                            </Badge>
                                            <Badge colorScheme={getBadgeColor(selectedJob.job_tag)} borderRadius="full" px={2}>
                                                {selectedJob.job_tag}
                                            </Badge>
                                        </HStack>
                                    </Box>
                                </Flex>
                                <Divider />
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>
                                            Job Type
                                        </Text>
                                        <Tag colorScheme="brand" size="lg" borderRadius="full">
                                            <TagLeftIcon boxSize="12px" as={Briefcase} />
                                            <TagLabel>{selectedJob.job_type}</TagLabel>
                                        </Tag>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>
                                            Published Status
                                        </Text>
                                        <Tag colorScheme={selectedJob.is_published ? "green" : "red"} size="lg" borderRadius="full">
                                            <TagLeftIcon boxSize="12px" as={selectedJob.is_published ? Check : Clock} />
                                            <TagLabel>{selectedJob.is_published ? "Published" : "Not Published"}</TagLabel>
                                        </Tag>
                                    </Box>
                                </SimpleGrid>
                                <Divider />
                                <Box>
                                    <Text fontWeight="bold" mb={2}>
                                        Salary
                                    </Text>
                                    <HStack>
                                        <Tag size="lg" variant="subtle" colorScheme="green" borderRadius="full">
                                            <TagLeftIcon boxSize="12px" as={DollarSign} />
                                            <TagLabel>
                                                {selectedJob.salary_min_amount
                                                    ? `${formatCurrency(selectedJob.salary_min_amount)} - ${formatCurrency(
                                                        selectedJob.salary_max_amount,
                                                    )} ${selectedJob.salary_per_type}`
                                                    : "Not specified"}
                                            </TagLabel>
                                        </Tag>
                                    </HStack>
                                </Box>
                                <Box>
                                    <Text fontWeight="bold" mb={2}>
                                        Location
                                    </Text>
                                    <Wrap>
                                        {selectedJob.job_locations?.map((location) => (
                                            <WrapItem key={location.uuid}>
                                                <Tag size="lg" variant="subtle" colorScheme="blue" borderRadius="full">
                                                    <TagLeftIcon boxSize="12px" as={MapPin} />
                                                    <TagLabel>{location.job_location?.title}</TagLabel>
                                                </Tag>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                </Box>
                                <Box>
                                    <Text fontWeight="bold" mb={2}>
                                        Dates
                                    </Text>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <Box bg={postedOnBg} p={3} borderRadius="md">
                                            <Text fontSize="sm" color={subtleTextColor}>
                                                Posted On
                                            </Text>
                                            <Flex align="center" mt={1}>
                                                <Calendar size={14} className="mr-2" color={accentColor} />
                                                <Text fontWeight="medium">{formatDate(selectedJob.job_post_date)}</Text>
                                            </Flex>
                                        </Box>
                                        <Box bg={postedOnBg} p={3} borderRadius="md">
                                            <Text fontSize="sm" color={subtleTextColor}>
                                                Published On
                                            </Text>
                                            <Flex align="center" mt={1}>
                                                <Clock size={14} className="mr-2" color={accentColor} />
                                                <Text fontWeight="medium">{formatDate(selectedJob.published_at) || "Not published"}</Text>
                                            </Flex>
                                        </Box>
                                    </SimpleGrid>
                                </Box>
                                {selectedJob.status === "active" && !selectedJob.is_published && (
                                    <Alert status="warning" borderRadius="md">
                                        <AlertIcon />
                                        <Box>
                                            <AlertTitle>Attention Required!</AlertTitle>
                                            <AlertDescription>
                                                This job is active but not published. Consider publishing it to make it visible to candidates.
                                            </AlertDescription>
                                        </Box>
                                    </Alert>
                                )}
                            </VStack>
                        </ModalBody>
                        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                            <Button variant="outline" mr={3} onClick={onClose} borderRadius="full">
                                Close
                            </Button>
                            <Button colorScheme="brand" borderRadius="full" leftIcon={<Edit size={16} />}>
                                Edit Job
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </ChakraProvider>
    )
}

// Premium Stat Card
const PremiumStatCard = ({ title, value, icon, change, timeframe, color, percentage, subtext }) => {
    const colorScheme = {
        brand: {
            bg: useColorModeValue("brand.50", "brand.900"),
            iconBg: useColorModeValue("brand.500", "brand.200"),
            iconColor: useColorModeValue("white", "gray.800"),
            textColor: useColorModeValue("brand.700", "brand.200"),
        },
        purple: {
            bg: useColorModeValue("purple.50", "purple.900"),
            iconBg: useColorModeValue("purple.500", "purple.200"),
            iconColor: useColorModeValue("white", "gray.800"),
            textColor: useColorModeValue("purple.700", "purple.200"),
        },
        accent: {
            bg: useColorModeValue("orange.50", "orange.900"),
            iconBg: useColorModeValue("orange.500", "orange.200"),
            iconColor: useColorModeValue("white", "gray.800"),
            textColor: useColorModeValue("orange.700", "orange.200"),
        },
        success: {
            bg: useColorModeValue("green.50", "green.900"),
            iconBg: useColorModeValue("green.500", "green.200"),
            iconColor: useColorModeValue("white", "gray.800"),
            textColor: useColorModeValue("green.700", "green.200"),
        },
    }
    const isPositive = change > 0
    const textColor = useColorModeValue("gray.800", "white")
    const subtleTextColor = useColorModeValue("gray.600", "gray.400")
    return (
        <Card
            bg={colorScheme[color].bg}
            borderRadius="xl"
            boxShadow="sm"
            overflow="hidden"
            borderColor={useColorModeValue(`${color}.100`, `${color}.800`)}
        >
            <CardBody>
                <Flex align="start">
                    <Center
                        bg={colorScheme[color].iconBg}
                        color={colorScheme[color].iconColor}
                        borderRadius="lg"
                        boxSize="48px"
                        mr={4}
                    >
                        {icon}
                    </Center>
                    <Box className="space-y-1">
                        <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                            {value}
                        </Text>
                        <Flex align="center" mt={1}>
                            <Flex align="center">
                                {isPositive ? (
                                    <ArrowUp color="green.600" size={12} style={{ marginRight: "5px" }} />
                                ) : (
                                    <ArrowRight color="red.600" size={12} style={{ marginRight: "5px" }} />
                                )}
                                <Text fontSize="xs">{Math.abs(change)}%</Text>
                            </Flex>
                            <Text fontSize="xs" color={subtleTextColor} ml={1}>
                                {timeframe}
                            </Text>
                        </Flex>
                    </Box>
                </Flex>
                <Box mt={4}>
                    <Flex justify="space-between" mb={1}>
                        <Text fontSize="xs" color={subtleTextColor}>
                            {subtext}
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color={colorScheme[color].textColor}>
                            {percentage}%
                        </Text>
                    </Flex>
                    <Progress value={percentage} size="xs" colorScheme={color} borderRadius="full" hasStripe />
                </Box>
            </CardBody>
        </Card>
    )
}

// Featured Stat
const FeaturedStat = ({ title, value, icon, description, color }) => {
    const bgColor = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.700")
    const textColor = useColorModeValue("gray.800", "white")
    const subtleTextColor = useColorModeValue("gray.600", "gray.400")
    return (
        <Card borderRadius="xl" boxShadow="sm" overflow="hidden" borderColor={borderColor}>
            <CardBody>
                <Flex align="center">
                    <Center bg={`${color}15`} color={color} borderRadius="md" boxSize="40px" mr={3}>
                        {icon}
                    </Center>
                    <Box>
                        <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                            {value}
                        </Text>
                        <Text fontSize="xs" color={subtleTextColor} mt={1}>
                            {description}
                        </Text>
                    </Box>
                </Flex>
            </CardBody>
        </Card>
    )
}

// Premium Jobs Table
const PremiumJobsTable = ({ jobs, onJobClick }) => {
    const borderColor = useColorModeValue("gray.200", "gray.700")
    const hoverBg = useColorModeValue("gray.50", "gray.700")
    const textColor = useColorModeValue("gray.800", "white")
    const subtleTextColor = useColorModeValue("gray.600", "gray.400")
    return (
        <Table variant="simple">
            <Thead>
                <Tr>
                    <Th>Job</Th>
                    <Th>Company</Th>
                    <Th>Type</Th>
                    <Th>Location</Th>
                    <Th>Status</Th>
                    <Th>Date</Th>
                    <Th>Actions</Th>
                </Tr>
            </Thead>
            <Tbody>
                {jobs.map((job) => (
                    <Tr key={job.uuid} _hover={{ bg: hoverBg }} cursor="pointer">
                        <Td onClick={() => onJobClick(job)}>
                            <VStack align="start" spacing={1}>
                                <Text fontWeight="medium" color={textColor}>
                                    {job.job_name}
                                </Text>
                                <Text fontSize="xs" color={subtleTextColor}>
                                    {job.job_title?.title}
                                </Text>
                            </VStack>
                        </Td>
                        <Td>
                            <Flex align="center">
                                <Avatar
                                    size="sm"
                                    name={job.company_name}
                                    src={job.company_logo ? `/company-logos/${job.company_logo}` : ""}
                                    bg="brand.100"
                                >
                                    {job.status === "active" && <AvatarBadge boxSize="1em" bg="green.500" />}
                                </Avatar>
                                <Text ml={2} fontSize="sm" color={textColor}>
                                    {job.company_name}
                                </Text>
                            </Flex>
                        </Td>
                        <Td>
                            <VStack align="start" spacing={1}>
                                <Text fontSize="sm" color={textColor}>
                                    {job.job_type}
                                </Text>
                                <Badge colorScheme={getBadgeColor(job.job_tag)} borderRadius="full">
                                    {job.job_tag}
                                </Badge>
                            </VStack>
                        </Td>
                        <Td>
                            <Flex align="center">
                                <MapPin size={12} className="mr-1" color={subtleTextColor} />
                                <Text fontSize="sm" color={textColor}>
                                    {job.job_locations?.[0]?.job_location?.title || "N/A"}
                                </Text>
                            </Flex>
                        </Td>
                        <Td>
                            <Badge
                                colorScheme={job.status === "active" ? "green" : job.status === "completed" ? "blue" : "gray"}
                                borderRadius="full"
                                px={2}
                            >
                                {job.status}
                            </Badge>
                        </Td>
                        <Td>
                            <Flex align="center">
                                <Calendar size={12} className="mr-1" color={subtleTextColor} />
                                <Text fontSize="sm" color={textColor}>
                                    {formatDate(job.job_post_date)}
                                </Text>
                            </Flex>
                        </Td>
                        <Td>
                            <HStack>
                                <IconButton
                                    aria-label="Edit"
                                    icon={<Edit size={16} />}
                                    size="sm"
                                    colorScheme="brand"
                                    variant="ghost"
                                    borderRadius="full"
                                />
                                <IconButton
                                    aria-label="View"
                                    icon={<Eye size={16} />}
                                    size="sm"
                                    colorScheme="green"
                                    variant="ghost"
                                    borderRadius="full"
                                    onClick={() => onJobClick(job)}
                                />
                            </HStack>
                        </Td>
                    </Tr>
                ))}
            </Tbody>
        </Table>
    )
}

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    })
}

// Helper function to format currency
const formatCurrency = (amount) => {
    if (!amount) return "0"
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(amount)
}

// Helper function to get badge color based on job tag
const getBadgeColor = (tag) => {
    switch (tag) {
        case "Internship":
            return "purple"
        case "Freelance":
            return "orange"
        case "Mid-Level":
            return "blue"
        case "Senior":
            return "green"
        default:
            return "gray"
    }
}

// Helper function to get progress color
const getProgressColor = (index) => {
    const colors = ["blue", "green", "purple", "orange", "teal", "cyan"]
    return colors[index % colors.length]
}

export default AdminProfile
